import { Button, Space, Table, Typography, notification, Modal, Card, Row, Col, Statistic, Input, Select } from "antd";
import { useHistory ,Link } from "react-router-dom";
import { DeleteOutlined, CloseCircleOutlined ,CheckCircleOutlined, EyeOutlined, UserOutlined, CrownOutlined, SearchOutlined} from '@ant-design/icons';
import { useEffect, useState, useCallback, useRef } from "react";
import ajaxService from "../../../services/ajax-service";
import moment from "moment";
import { Filter } from "../../../components";
import { calculateTableScrollX, calculateModalWidth } from "../../../utils/widthCalculator";
import { useTableHover } from "../../../hooks/useTableHover";

const { Text, Title } = Typography

const FREE_USER = "free-user";
const PAID_USER = "paid-user";

const Users = () => {

    let history = useHistory()
    
    // Use custom table hover hook to prevent fixed column overlap
    useTableHover();

    const [filter, setFilter] = useState('')
    const [statusFilter, setStatusFilter] = useState('all')
    const [searchValue, setSearchValue] = useState('')
    const debounceTimer = useRef(null)

    const [type, setType] = useState(null)
    const [dataSource, setDataSource] = useState({});
    const [additionalData, setAdditionalData] = useState({
        total_users: 0,
        free_users: 0,
        premium_users: 0,
        active_users: 0
    });

    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    const columns = [
        
        {
            title: "SNo",
            dataIndex: "id",
            key: "id",
            width: 70,
            fixed: 'left',
            render: (text, record, index) => (
                <Text style={{ fontSize: '0.85rem' }}>{(dataSource.current_page - 1) * dataSource.per_page + index + 1}</Text>
            ),
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 180,
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
            sortDirections: ['ascend', 'descend'],
            ellipsis: true,
            render: (name) => <Text style={{ fontSize: '0.85rem' }}>{name}</Text>,
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            width: 240,
            sorter: (a, b) => (a.email || '').localeCompare(b.email || ''),
            sortDirections: ['ascend', 'descend'],
            ellipsis: true,
            render: (email) => <Text style={{ fontSize: '0.85rem' }}>{email}</Text>,
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
            width: 160,
            ellipsis: true,
            render: (phone, record) => {
                const code = record?.country_code_phone || record?.country?.country_code;
                return <Text style={{ fontSize: '0.85rem' }}>{code ? '+' + code + ' ' + (phone || '') : (phone || 'N/A')}</Text>;
            },
        },
        {
            title: 'Plan',
            dataIndex: 'stripe_id',
            key: 'stripe_id',
            width: 110,
            render: (stripe_id) => (<Text style={{ fontSize: '0.85rem' }}>{(stripe_id) ? 'Premium' : 'Free'}</Text>),
            filters: [
                { text: 'Free User', value: 'free' },
                { text: 'Paid User', value: 'paid' },
            ],
            onFilter: (value, record) => value === 'free' ? !record.stripe_id : !!record.stripe_id,
        },
        {
            title: 'Status',
            dataIndex: 'active',
            key: 'active',
            width: 120,
            render: (active) => (<Text style={{ fontSize: '0.85rem' }}>{(active) ? 'Active' : 'Inactive'}</Text>),
            filters: [
                { text: 'Active', value: true },
                { text: 'Inactive', value: false },
            ],
            onFilter: (value, record) => record.active === value,
        },
        {
            title: 'Created',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 110,
            sorter: (a, b) => moment(a.created_at).unix() - moment(b.created_at).unix(),
            sortDirections: ['ascend', 'descend'],
            render: (created_at) => (<Text style={{ fontSize: '0.8rem' }}>{moment(created_at).format('DD/MM/YY')}</Text>)
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 100,
            fixed: 'right',
            render: (text, record) => (
                <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                    <Link to={`/users/${record.id}`}>
                        <Button
                            type="text"
                            size="small"
                            style={{
                                padding: '4px 8px',
                                fontSize: '12px',
                                color: 'var(--primary-purple)',
                                border: '1px solid var(--primary-purple)',
                                borderRadius: 'var(--radius-md)',
                                transition: 'var(--transition-fast)'
                            }}
                            icon={<EyeOutlined style={{ fontSize: '12px' }} />}
                        />
                    </Link>
                    <Button
                        type="text"
                        size="small"
                        style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: 'var(--error-red)',
                            border: '1px solid var(--error-red)',
                            borderRadius: 'var(--radius-md)',
                            transition: 'var(--transition-fast)'
                        }}
                        icon={<DeleteOutlined style={{ fontSize: '12px' }} />}
                        onClick={() => handleDeleteClick(record.id)}
                    />
                </div>
            )
        },
    ];

    const handleDeleteClick = (id) => {
        setUserToDelete(id);
        setDeleteModalVisible(true);
    };

    const handleConfirmDelete = async () => {
        if (userToDelete) {
            try {
                const response = await ajaxService.delete(`/users/`, userToDelete);

                const { success, data } = response;
                console.log('response', response);
                if (success) {
                    notification.open({ message: `User deleted successfully!`, icon: <CheckCircleOutlined style={{ color: 'var(--success-green)' }} /> });
                    initialize(type);
                } else {
                    notification.open({ message: `Failed to delete user!`, icon: <CloseCircleOutlined style={{ color: 'var(--error-red)' }} /> });
                }
            } catch (error) {
                console.error('Delete error:', error);
                notification.open({ message: `Failed to delete user!`, icon: <CloseCircleOutlined style={{ color: 'var(--error-red)' }} /> });
            }
        }
        setDeleteModalVisible(false);
        setUserToDelete(null);
    };

    const buildQueryParams = (userType, name, page = null, status = null) => {
        let params = [];
        if (userType) {
            params.push(`user_type=${userType}`);
        }
        if (name) {
            params.push(`name=${name.trim()}`);
        }
        if (page !== null) {
            params.push(`page=${page}`);
        }
        if (status && status !== 'all') {
            params.push(`status=${status}`);
        }
        return params.length > 0 ? `?${params.join('&')}` : '';
    };

    const handleSearch = async ({ name }) => {
        const searchName = name ? name.trim() : '';
        const queryParams = buildQueryParams(type, searchName, null, statusFilter);
        const response = await ajaxService.get(`/users${queryParams}`);

        const { success, data } = response;
        console.log('response',response);
        if (success) {
            if (data.data.length == 0 && searchName) {
                setFilter('')
                notification.open({ message: `${searchName} is not found!`, icon: <CloseCircleOutlined style={{ color: 'var(--error-red)' }} /> })
            } else {
                setFilter(searchName)
            }

            setDataSource(response.data)
        }
    }

    // Debounced search handler
    useEffect(() => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current)
        }

        // Only search if searchValue is provided or if filters changed
        if (searchValue !== undefined) {
            debounceTimer.current = setTimeout(() => {
                handleSearch({ name: searchValue })
            }, 500)
        }

        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current)
            }
        }
    }, [searchValue, type, statusFilter])

    const onClick = (newType) => {
        setType(newType)
        setFilter('')
        setSearchValue('')
        setStatusFilter('all')
        initialize(newType)
    }

    const handleChangePage = async (page) => {
        const queryParams = buildQueryParams(type, filter, page, statusFilter);
        const response = await ajaxService.get(`/users${queryParams}`);

        if (response.success) {
            setDataSource(response.data)
        }

    }

    const initialize = async (userType = type) => {
        const queryParams = buildQueryParams(userType, filter, null, statusFilter);
        const response = await ajaxService.get(`/users${queryParams}`);

        if (response.success) {
            setDataSource(response.data)
            if (response.additionalData) setAdditionalData(response.additionalData)
        }
    }

    useEffect(() => {
        initialize(type)
    }, [])

    // Listen for sidebar collapse state
    useEffect(() => {
        const handleSidebarCollapse = (event) => {
            setSidebarCollapsed(event.detail.collapsed);
        };

        window.addEventListener('sidebarCollapse', handleSidebarCollapse);
        return () => window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
    }, []);

    // Calculate table scroll width using standardized utility
    // Table columns total: 70 + 180 + 240 + 160 + 110 + 100 + 110 + 140 = 1110px
    const calculateTableWidth = useCallback(() => {
        return calculateTableScrollX(sidebarCollapsed, 800, 1110, 40);
    }, [sidebarCollapsed]);

    const [tableWidth, setTableWidth] = useState(() => calculateTableScrollX(false, 800, 1110, 40));

    // Recalculate on window resize and sidebar toggle
    useEffect(() => {
        const handleResize = () => {
            setTableWidth(calculateTableWidth());
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Initial calculation
        
        return () => window.removeEventListener('resize', handleResize);
    }, [calculateTableWidth]);

    return (
        <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
            {/* Header Section */}
            <div className="module-header">
                <h1 className="module-title">User Management</h1>
                <p className="module-subtitle">Manage and monitor all platform users</p>
            </div>

            {/* Statistics Cards */}
            <div className="stats-grid">
                <div className="stats-card">
                    <div className="stats-value">{additionalData.total_users ?? 0}</div>
                    <div className="stats-label">Total Users</div>
                    <UserOutlined style={{ color: 'var(--primary-purple)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
                </div>
                <div className="stats-card">
                    <div className="stats-value">{additionalData.free_users ?? 0}</div>
                    <div className="stats-label">Free Users</div>
                    <UserOutlined style={{ color: 'var(--text-gray)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
                </div>
                <div className="stats-card">
                    <div className="stats-value">{additionalData.premium_users ?? 0}</div>
                    <div className="stats-label">Premium Users</div>
                    <CrownOutlined style={{ color: 'var(--accent-purple)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
                </div>
                <div className="stats-card">
                    <div className="stats-value">{additionalData.active_users ?? 0}</div>
                    <div className="stats-label">Active Users</div>
                    <CheckCircleOutlined style={{ color: 'var(--success-green)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
                </div>
            </div>

            {/* Filters and Search */}
            <div className="filter-section">
                <div className="filter-row">
                    <div className="filter-item">
                        <Input
                            placeholder="Search by name..."
                            prefix={<SearchOutlined style={{ color: 'var(--text-tertiary)' }} />}
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onPressEnter={(e) => {
                                setSearchValue(e.target.value)
                                handleSearch({ name: e.target.value })
                            }}
                            className="input-field"
                            allowClear
                        />
                    </div>
                    <div className="filter-item">
                        <Select
                            placeholder="Filter by status"
                            style={{ width: '100%' }}
                            className="input-field"
                            value={statusFilter}
                            onChange={async (value) => {
                                setStatusFilter(value);
                                const searchName = searchValue ? searchValue.trim() : '';
                                const queryParams = buildQueryParams(type, searchName, null, value);
                                const response = await ajaxService.get(`/users${queryParams}`);
                                if (response.success) {
                                    setDataSource(response.data);
                                    if (response.additionalData) setAdditionalData(response.additionalData);
                                }
                            }}
                        >
                            <Select.Option value="all">All Users</Select.Option>
                            <Select.Option value="active">Active</Select.Option>
                            <Select.Option value="inactive">Inactive</Select.Option>
                        </Select>
                    </div>
                    <div className="filter-actions">
                        <Button
                            className={type === FREE_USER ? "btn-secondary-active" : "btn-secondary"}
                            size="small"
                            onClick={() => onClick(FREE_USER)}
                        >
                            Free Users
                        </Button>
                        <Button
                            className={type === PAID_USER ? "btn-secondary-active" : "btn-secondary"}
                            size="small"
                            onClick={() => onClick(PAID_USER)}
                        >
                            Premium Users
                        </Button>
                    </div>
                </div>
            </div>

            {/* Users Table */}
            <div className={`data-table-card ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
                <Table
                    columns={columns}
                    dataSource={dataSource.data}
                    className="data-table"
                    rowKey='id'
                    pagination={{
                        onChange: (page, size) => handleChangePage(page),
                        current: dataSource.current_page,
                        pageSize: dataSource.per_page,
                        total: dataSource.total,
                        className: "data-table-pagination",
                        showSizeChanger: false,
                        showQuickJumper: false,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        size: 'small'
                    }}
                    scroll={{ x: tableWidth }}
                    size="small"
                    bordered={false}
                />
            </div>

            <Modal
                className="modal-overlay"
                width={calculateModalWidth('400px')}
                open={deleteModalVisible}
                onCancel={() => setDeleteModalVisible(false)}
                footer={null}
                centered
            >
                <div style={{ 
                    padding: "var(--spacing-2xl)",
                    background: 'var(--dark-bg-card)',
                    borderRadius: 'var(--radius-lg)',
                    textAlign: 'center'
                }}>
                    <div style={{ 
                        marginBottom: 'var(--spacing-xl)',
                        fontSize: '3rem',
                        color: 'var(--error-red)'
                    }}>
                        <DeleteOutlined />
                    </div>
                    <Title level={4} style={{ 
                        color: 'var(--text-white)', 
                        marginBottom: 'var(--spacing-md)',
                        fontFamily: 'Poppins-Bold'
                    }}>
                        Confirm Delete
                    </Title>
                    <p style={{ 
                        color: 'var(--text-gray)', 
                        marginBottom: 'var(--spacing-xl)',
                        fontSize: '0.95rem',
                        lineHeight: 1.6
                    }}>
                        Are you sure you want to delete this user? This action cannot be undone.
                    </p>
                    <div style={{ 
                        display: 'flex',
                        gap: 'var(--spacing-md)',
                        justifyContent: 'center'
                    }}>
                        <Button
                            onClick={() => setDeleteModalVisible(false)}
                            style={{
                                minWidth: '120px',
                                height: '40px',
                                background: 'var(--dark-bg-hover)',
                                borderColor: 'rgba(254, 2, 125, 0.3)',
                                color: 'var(--text-white)'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            danger
                            onClick={handleConfirmDelete}
                            style={{
                                minWidth: '120px',
                                height: '40px',
                                background: 'var(--error-red)',
                                borderColor: 'var(--error-red)'
                            }}
                        >
                            Delete
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    )
}
export default Users